import React, { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Card, CardBody, CardHeader, CardTitle, Col, Row, Table, Button, Pagination, PaginationItem, PaginationLink, FormGroup, Label, Input } from "reactstrap"
import { setBreadcrumbItems } from "store/actions"
import { connect } from "react-redux"
import apiClient from "helpers/apiClient"
import { formatBidPrice, formatDisplayNumber, isNumericDisplayValue, parseIndianNumber } from "helpers/indianNumber"
import { formatIndianDate, formatIndianDateTime } from "helpers/indianDate"
import "../Listings/Listings.scss"

const leadSourceOptions = [
    { value: "", label: "All" },
    { value: "Website", label: "Website" },
    { value: "Portal", label: "Portal" },
    { value: "Walk-in", label: "Walk-in" },
    { value: "Referral", label: "Referral" },
]
const purposeOptions = [
    { value: "", label: "All" },
    { value: "Buy", label: "Buy" },
    { value: "Rent", label: "Rent" },
    { value: "Invest", label: "Invest" },
]
const transactionStatusOptions = [
    { value: "", label: "All" },
    { value: "Active", label: "Active" },
    { value: "Completed", label: "Completed" },
]
const brokerStatusOptions = [
    { value: "", label: "All" },
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
]

interface BreadcrumbItem {
    title: string
    link: string
}

interface CrmListProps {
    setBreadcrumbItems: (title: string, items: BreadcrumbItem[]) => void
}

const breadcrumbItems: BreadcrumbItem[] = [
    { title: "CRM", link: "#" },
    { title: "CRM List", link: "#" },
]

/** Flip to `false` when the CRM list is ready to use (stops showing the placeholder and enables API). */
const CRM_LIST_COMING_SOON = true

const CrmList = (props: CrmListProps) => {
    const navigate = useNavigate()

    const LIMIT = 10
    const [crmList, setCrmList] = useState<any[]>([])
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 })
    const [loading, setLoading] = useState(!CRM_LIST_COMING_SOON)
    const [error, setError] = useState<string | null>(null)
    const [selectedCrm, setSelectedCrm] = useState<any | null>(null)
    const [page, setPage] = useState(1)
    const [filters, setFilters] = useState({ leadSource: "", purpose: "", transactionStatus: "", brokerStatus: "" })
    const [appliedFilters, setAppliedFilters] = useState({ leadSource: "", purpose: "", transactionStatus: "", brokerStatus: "" })

    useEffect(() => {
        props.setBreadcrumbItems(CRM_LIST_COMING_SOON ? "CRM (Coming soon)" : "CRM List", breadcrumbItems)
    }, [props])

    const fetchList = useCallback(() => {
        setLoading(true)
        setError(null)
        const params = new URLSearchParams()
        params.set("page", String(page))
        params.set("limit", String(LIMIT))
        if (appliedFilters.leadSource) params.set("leadSource", appliedFilters.leadSource)
        if (appliedFilters.purpose) params.set("purpose", appliedFilters.purpose)
        if (appliedFilters.transactionStatus) params.set("transactionStatus", appliedFilters.transactionStatus)
        if (appliedFilters.brokerStatus) params.set("brokerStatus", appliedFilters.brokerStatus)
        apiClient.get(`/api/crm/getCrmList?${params.toString()}`)
            .then((res) => {
                const raw = res?.data?.data ?? res?.data
                setCrmList(Array.isArray(raw) ? raw : [])
                const pag = res?.data?.pagination
                if (pag) setPagination({ page: pag.page ?? 1, limit: pag.limit ?? LIMIT, total: pag.total ?? 0, totalPages: pag.totalPages ?? 1 })
            })
            .catch((err) => setError(err?.response?.data?.message || err?.message || "Failed to load CRM list"))
            .finally(() => setLoading(false))
    }, [page, appliedFilters])

    useEffect(() => {
        if (CRM_LIST_COMING_SOON) return
        fetchList()
    }, [fetchList])

    const handleSearch = () => {
        setAppliedFilters(filters)
        setPage(1)
    }

    const handleReset = () => {
        const empty = { leadSource: "", purpose: "", transactionStatus: "", brokerStatus: "" }
        setFilters(empty)
        setAppliedFilters(empty)
        setPage(1)
    }

    const updateFilter = (key: string, value: string) => {
        setFilters((f) => ({ ...f, [key]: value }))
    }

    const showDetail = (item: any) => setSelectedCrm(item)
    const hideDetail = () => setSelectedCrm(null)

    const formatDate = (d: string | undefined) => formatIndianDate(d)

    const CRM_PRICE_LABELS = new Set(["Budget", "Price", "Rate per Sq.ft", "Revenue Generated", "Value", "Booking Amount"])
    const CRM_AREA_LABELS = new Set(["Carpet Area", "Built Up Area", "Super Area"])

    const formatCrmField = (label: string, value: React.ReactNode): React.ReactNode => {
        if (value === undefined || value === null || value === "") return null
        if (!isNumericDisplayValue(value)) return value
        const numericValue: string | number = typeof value === "number" ? value : String(value)
        const num = typeof value === "number" ? value : Number(parseIndianNumber(String(value)))
        if (CRM_PRICE_LABELS.has(label)) return formatBidPrice(num)
        if (CRM_AREA_LABELS.has(label)) return formatDisplayNumber(numericValue, { suffix: "sq.ft" })
        return formatDisplayNumber(numericValue)
    }

    const TableRow = ({ label, value }: { label: string; value: React.ReactNode }) => {
        const displayValue = formatCrmField(label, value)
        if (displayValue === null) return null
        return (
            <tr>
                <td className="listing-detail-table-label">{label}</td>
                <td className="listing-detail-table-value">{displayValue}</td>
            </tr>
        )
    }
    const SectionRow = ({ title }: { title: string }) => (
        <tr>
            <td colSpan={2} className="listing-detail-table-section">{title}</td>
        </tr>
    )

    const { total, totalPages } = pagination
    const startIdx = total === 0 ? 0 : (page - 1) * LIMIT + 1

    if (CRM_LIST_COMING_SOON) {
        return (
            <React.Fragment>
                <Row>
                    <Col xs="12">
                        <Card className="border-0 shadow-sm overflow-hidden">
                            <CardBody
                                className="d-flex flex-column align-items-center justify-content-center text-center px-4 py-5"
                                style={{
                                    minHeight: "min(72vh, 520px)",
                                    background: "linear-gradient(160deg, #f8fafc 0%, #e0e7ff 45%, #f1f5f9 100%)",
                                }}
                            >
                                <div
                                    className="rounded-circle d-flex align-items-center justify-content-center mb-4"
                                    style={{
                                        width: 88,
                                        height: 88,
                                        background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                                        boxShadow: "0 12px 40px rgba(99, 102, 241, 0.35)",
                                    }}
                                >
                                    <i className="mdi mdi-rocket-launch-outline text-white" style={{ fontSize: "2.5rem" }} aria-hidden />
                                </div>
                                <h4 className="fw-bold text-dark mb-2">CRM — Coming soon</h4>
                                <p className="text-muted mb-1 mx-auto" style={{ maxWidth: 440 }}>
                                    Lead pipeline, automations, and reports are being built. The full CRM list and filters will appear here once this module is live.
                                </p>
                                <p className="small text-muted mb-4">Thank you for your patience.</p>
                                <div className="d-flex flex-wrap gap-3 justify-content-center align-items-center">
                                    <span
                                        className="d-inline-flex align-items-center gap-2 rounded-pill px-4 py-2"
                                        style={{
                                            fontSize: "0.8125rem",
                                            fontWeight: 600,
                                            letterSpacing: "0.08em",
                                            textTransform: "uppercase",
                                            color: "#4338ca",
                                            background: "linear-gradient(180deg, #ffffff 0%, #f5f3ff 100%)",
                                            border: "1px solid rgba(99, 102, 241, 0.45)",
                                            boxShadow: "0 4px 14px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255,255,255,0.9)",
                                        }}
                                    >
                                        <span
                                            className="rounded-circle flex-shrink-0"
                                            style={{
                                                width: 8,
                                                height: 8,
                                                background: "linear-gradient(135deg, #6366f1, #a855f7)",
                                                boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.25)",
                                            }}
                                            aria-hidden
                                        />
                                        Stay tuned
                                    </span>
                              
                                    <button
                                        type="button"
                                        className="d-inline-flex align-items-center gap-2 rounded-pill px-4 py-2 border-0"
                                        style={{
                                            cursor: "pointer",
                                            fontWeight: 600,
                                            fontSize: "0.875rem",
                                            color: "#4338ca",
                                            background: "linear-gradient(180deg, #ffffff 0%, #f5f3ff 100%)",
                                            border: "1px solid rgba(99, 102, 241, 0.45)",
                                            boxShadow: "0 4px 14px rgba(99, 102, 241, 0.12), inset 0 1px 0 rgba(255,255,255,0.9)",
                                        }}
                                        onClick={() => navigate(-1)}
                                    >
                                        <i className="mdi mdi-arrow-left" style={{ fontSize: "1.1rem" }} aria-hidden />
                                        Go back
                                    </button>
                                    <button
                                        type="button"
                                        className="d-inline-flex align-items-center gap-2 rounded-pill px-4 py-2 border-0 text-white"
                                        style={{
                                            cursor: "pointer",
                                            fontWeight: 600,
                                            fontSize: "0.875rem",
                                            background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                                            boxShadow: "0 4px 18px rgba(99, 102, 241, 0.4)",
                                        }}
                                        onClick={() => navigate("/dashboard")}
                                    >
                                        <i className="mdi mdi-view-dashboard-outline" style={{ fontSize: "1.1rem" }} aria-hidden />
                                        Dashboard
                                    </button>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            <Row>
                <Col xs="12">
                    <Card>
                        <CardHeader className="d-flex justify-content-between align-items-center">
                            <CardTitle tag="h5" className="mb-0">All CRM</CardTitle>
                            <div className="d-flex align-items-center gap-2">
                                <span className="text-muted">{total} record(s)</span>
                                <Button color="primary" size="sm" onClick={() => navigate("/crm/create")}>Add New</Button>
                            </div>
                        </CardHeader>
                        <CardBody>
                            <Row className="mb-3 g-3 align-items-end">
                                <Col sm="6" md="3" lg="3">
                                    <FormGroup className="mb-0">
                                        <Label className="small mb-1">Lead Source</Label>
                                        <Input type="select" value={filters.leadSource} onChange={(e) => updateFilter("leadSource", e.target.value)} >
                                            {leadSourceOptions.map((o) => (
                                                <option key={o.value || "all"} value={o.value}>{o.label}</option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                                <Col sm="6" md="3" lg="3">
                                    <FormGroup className="mb-0">
                                        <Label className="small mb-1">Purpose</Label>
                                        <Input type="select" value={filters.purpose} onChange={(e) => updateFilter("purpose", e.target.value)} >
                                            {purposeOptions.map((o) => (
                                                <option key={o.value || "all"} value={o.value}>{o.label}</option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                                <Col sm="6" md="3" lg="3">
                                    <FormGroup className="mb-0">
                                        <Label className="small mb-1">Transaction Status</Label>
                                        <Input type="select" value={filters.transactionStatus} onChange={(e) => updateFilter("transactionStatus", e.target.value)} >
                                            {transactionStatusOptions.map((o) => (
                                                <option key={o.value || "all"} value={o.value}>{o.label}</option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                                <Col sm="6" md="3" lg="3">
                                    <FormGroup className="mb-0">
                                        <Label className="mb-1">Broker Status</Label>
                                        <Input type="select" value={filters.brokerStatus} onChange={(e) => updateFilter("brokerStatus", e.target.value)} >
                                            {brokerStatusOptions.map((o) => (
                                                <option key={o.value || "all"} value={o.value}>{o.label}</option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                                <Col sm="12" md="12" lg="12" className=" mb-3 d-flex gap-2 justify-content-end ">
                                    <Button color="primary" onClick={handleSearch}>
                                        <i className="mdi mdi-magnify me-1" /> Search
                                    </Button>
                                    <Button color="secondary" outline onClick={handleReset}>
                                        <i className="mdi mdi-refresh me-1" /> Reset
                                    </Button>
                                </Col>
                            </Row>
                            {loading && <div className="alert alert-info">Loading CRM...</div>}
                            {error && <div className="alert alert-danger">{error}</div>}
                            {!loading && !error && (
                                <div className="table-responsive">
                                    <Table className="table table-bordered align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: 50 }}>S.No.</th>
                                                <th style={{ width: 140, minWidth: 140 }}>Action</th>
                                                <th>Lead Name</th>
                                                <th>Mobile</th>
                                                <th>Email</th>
                                                <th>Purpose</th>
                                                <th>Budget</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {crmList.length === 0 ? (
                                                <tr>
                                                    <td colSpan={7} className="text-center text-muted py-4">No CRM records found</td>
                                                </tr>
                                            ) : (
                                                crmList.map((item, idx) => (
                                                    <React.Fragment key={item._id || idx}>
                                                        <tr>
                                                            <td>{(page - 1) * LIMIT + idx + 1}</td>
                                                            <td style={{ width: 140, minWidth: 140, textAlign: "center" }}>
                                                                {selectedCrm?._id === item._id ? (
                                                                    <Button color="secondary" className="text-nowrap" size="sm" onClick={hideDetail}>
                                                                        <i className="mdi mdi-close me-1" /> Hide details
                                                                    </Button>
                                                                ) : (
                                                                    <Button color="primary" size="sm" onClick={() => showDetail(item)}>View more</Button>
                                                                )}
                                                            </td>
                                                            <td><strong>{item.leadName || "—"}</strong></td>
                                                            <td>{item.mobile || "—"}</td>
                                                            <td>{item.email || "—"}</td>
                                                            <td>{item.purpose || "—"}</td>
                                                            <td>{isNumericDisplayValue(item.budget) ? formatBidPrice(Number(parseIndianNumber(String(item.budget)))) : (item.budget || "—")}</td>
                                                        </tr>
                                                        {selectedCrm?._id === item._id && (
                                                            <tr>
                                                                <td colSpan={7} className="p-0 align-top border-top-0">
                                                                    <div className=" m-2 p-3">
                                                                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                                                                            <h6 className="mb-0 text-dark d-flex align-items-center">
                                                                                <i className="mdi mdi-account-tie me-2 text-primary" />
                                                                                {selectedCrm.leadName || "CRM Details"}
                                                                            </h6>
                                                                            <Button color="primary" size="sm" onClick={() => navigate(`/crm/${selectedCrm._id}`)}>
                                                                                <i className="mdi mdi-pencil me-1" /> Edit
                                                                            </Button>
                                                                        </div>
                                                                        <div className="listing-detail-metrics mb-3">
                                                                            <div className="listing-detail-metric">
                                                                                <div className="listing-detail-metric-label">Lead Name</div>
                                                                                <div className="listing-detail-metric-value primary">{selectedCrm.leadName || "—"}</div>
                                                                            </div>
                                                                            <div className="listing-detail-metric">
                                                                                <div className="listing-detail-metric-label">Budget</div>
                                                                                <div className="listing-detail-metric-value">{selectedCrm.budget || "—"}</div>
                                                                            </div>
                                                                            <div className="listing-detail-metric">
                                                                                <div className="listing-detail-metric-label">Purpose</div>
                                                                                <div className="listing-detail-metric-value">{selectedCrm.purpose || "—"}</div>
                                                                            </div>
                                                                            <div className="listing-detail-metric">
                                                                                <div className="listing-detail-metric-label">Mobile</div>
                                                                                <div className="listing-detail-metric-value">{selectedCrm.mobile || "—"}</div>
                                                                            </div>
                                                                        </div>
                                                                        <Row>
                                                                            <Col md="6">
                                                                                <table className="listing-detail-table">
                                                                                    <tbody>
                                                                                        <SectionRow title="Lead Master" />
                                                                                        <TableRow label="Lead Name" value={selectedCrm.leadName} />
                                                                                        <TableRow label="Mobile" value={selectedCrm.mobile} />
                                                                                        <TableRow label="Email" value={selectedCrm.email} />
                                                                                        <TableRow label="Budget" value={selectedCrm.budget} />
                                                                                        <TableRow label="Purpose" value={selectedCrm.purpose} />
                                                                                        <TableRow label="Property Type" value={selectedCrm.propertyType} />
                                                                                        <TableRow label="Preferred Location" value={selectedCrm.preferredLocation} />
                                                                                        <TableRow label="Lead Source" value={selectedCrm.leadSource} />
                                                                                        <TableRow label="Follow Up Date" value={formatDate(selectedCrm.followUpDate)} />
                                                                                        <TableRow label="Notes" value={selectedCrm.notes} />
                                                                                        <SectionRow title="Inventory / New Property" />
                                                                                        <TableRow label="Inventory Source" value={selectedCrm.inventorySource} />
                                                                                        <TableRow label="Project Name" value={selectedCrm.projectName} />
                                                                                        <TableRow label="Developer Name" value={selectedCrm.developerName} />
                                                                                        <TableRow label="Property Type" value={selectedCrm.newPropertyType} />
                                                                                        <TableRow label="Unit Number" value={selectedCrm.unitNumber} />
                                                                                        <TableRow label="Floor" value={selectedCrm.floor} />
                                                                                        <TableRow label="Carpet Area" value={selectedCrm.carpetArea} />
                                                                                        <TableRow label="Built Up Area" value={selectedCrm.builtUpArea} />
                                                                                        <TableRow label="Super Area" value={selectedCrm.superArea} />
                                                                                        <TableRow label="Facing" value={selectedCrm.facing} />
                                                                                        <TableRow label="Price" value={selectedCrm.price} />
                                                                                        <TableRow label="Rate per Sq.ft" value={selectedCrm.ratePerSqft} />
                                                                                        <TableRow label="Availability" value={selectedCrm.availabilityStatus} />
                                                                                        <TableRow label="Possession Date" value={formatDate(selectedCrm.possessionDate)} />
                                                                                        <TableRow label="Amenities" value={selectedCrm.amenities} />
                                                                                        <TableRow label="Parking" value={selectedCrm.parking} />
                                                                                        <TableRow label="Legal Status" value={selectedCrm.legalStatus} />
                                                                                    </tbody>
                                                                                </table>
                                                                            </Col>
                                                                            <Col md="6">
                                                                                <table className="listing-detail-table">
                                                                                    <tbody>
                                                                                        <SectionRow title="Client / Customer" />
                                                                                        <TableRow label="Client Name" value={selectedCrm.clientName} />
                                                                                        <TableRow label="Mobile" value={selectedCrm.clientMobile} />
                                                                                        <TableRow label="Email" value={selectedCrm.clientEmail} />
                                                                                        <TableRow label="Address" value={selectedCrm.address} />
                                                                                        <TableRow label="Buyer Type" value={selectedCrm.buyerType} />
                                                                                        <TableRow label="Past Transactions" value={selectedCrm.pastTransactions} />
                                                                                        <TableRow label="Notes" value={selectedCrm.clientNotes} />
                                                                                        <TableRow label="Convert to Lead" value={selectedCrm.convertToLead} />
                                                                                        <SectionRow title="Broker" />
                                                                                        <TableRow label="Broker Name" value={selectedCrm.brokerName} />
                                                                                        <TableRow label="Company" value={selectedCrm.companyName} />
                                                                                        <TableRow label="Contact Person" value={selectedCrm.contactPerson} />
                                                                                        <TableRow label="Mobile" value={selectedCrm.brokerMobile} />
                                                                                        <TableRow label="Email" value={selectedCrm.brokerEmail} />
                                                                                        <TableRow label="City" value={selectedCrm.city} />
                                                                                        <TableRow label="Projects Assigned" value={selectedCrm.projectsAssigned} />
                                                                                        <TableRow label="Status" value={selectedCrm.brokerStatus} />
                                                                                        <TableRow label="Leads Assigned" value={selectedCrm.leadsAssigned} />
                                                                                        <TableRow label="Deals Closed" value={selectedCrm.dealsClosed} />
                                                                                        <TableRow label="Revenue Generated" value={selectedCrm.revenueGenerated} />
                                                                                        <SectionRow title="Transaction" />
                                                                                        <TableRow label="Seller" value={selectedCrm.seller} />
                                                                                        <TableRow label="Buyer" value={selectedCrm.buyer} />
                                                                                        <TableRow label="Property" value={selectedCrm.property} />
                                                                                        <TableRow label="Value" value={selectedCrm.transactionValue} />
                                                                                        <TableRow label="Booking Amount" value={selectedCrm.bookingAmount} />
                                                                                        <TableRow label="Stage" value={selectedCrm.transactionStage} />
                                                                                        <TableRow label="Date" value={formatDate(selectedCrm.transactionDate)} />
                                                                                        <TableRow label="Status" value={selectedCrm.transactionStatus} />
                                                                                        <SectionRow title="Activity" />
                                                                                        <TableRow label="Type" value={selectedCrm.activityType} />
                                                                                        <TableRow label="Linked Lead/Client" value={selectedCrm.linkedLeadClient} />
                                                                                        <TableRow label="Date/Time" value={selectedCrm.activityDateTime ? formatIndianDateTime(selectedCrm.activityDateTime) : undefined} />
                                                                                        <TableRow label="Outcome" value={selectedCrm.outcome} />
                                                                                        <TableRow label="Next Action Date" value={formatDate(selectedCrm.nextActionDate)} />
                                                                                        <TableRow label="Notes" value={selectedCrm.activityNotes} />
                                                                                        <TableRow label="Geo-tag" value={selectedCrm.geotag} />
                                                                                        <SectionRow title="Communication" />
                                                                                        <TableRow label="WhatsApp" value={selectedCrm.whatsappIntegration ? "Yes" : "No"} />
                                                                                        <TableRow label="Email Templates" value={selectedCrm.emailTemplates ? "Yes" : "No"} />
                                                                                        <TableRow label="SMS Alerts" value={selectedCrm.smsAlerts ? "Yes" : "No"} />
                                                                                        <TableRow label="Reminder Automation" value={selectedCrm.reminderAutomation ? "Yes" : "No"} />
                                                                                        <SectionRow title="Documents" />
                                                                                        <TableRow label="Property" value={selectedCrm.documentProperty} />
                                                                                        <TableRow label="Document Name" value={selectedCrm.documentName} />
                                                                                        <TableRow label="Notes" value={selectedCrm.documentNotes} />
                                                                                    </tbody>
                                                                                </table>
                                                                            </Col>
                                                                        </Row>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            )}

                            {!loading && !error && crmList.length > 0 && totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                                    <small className="text-muted">
                                        Showing {startIdx}–{Math.min(startIdx + crmList.length - 1, total)} of {total}
                                    </small>
                                    <Pagination className="mb-0 pagination-sm">
                                        <PaginationItem disabled={page <= 1}>
                                            <PaginationLink previous onClick={() => setPage((p) => Math.max(1, p - 1))} />
                                        </PaginationItem>
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                                            <PaginationItem key={p} active={p === page}>
                                                <PaginationLink onClick={() => setPage(p)}>{p}</PaginationLink>
                                            </PaginationItem>
                                        ))}
                                        <PaginationItem disabled={page >= totalPages}>
                                            <PaginationLink next onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />
                                        </PaginationItem>
                                    </Pagination>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </React.Fragment>
    )
}

export default connect(null, { setBreadcrumbItems })(CrmList)
